import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Award, ShieldCheck, Sparkles, Store } from 'lucide-react';
import { getGameById, supportsGameGroups } from '@/config/games';
import { useGameStore } from '@/stores/game.store';
import { useAuthStore } from '@/stores/auth.store';
import { useGameGroupsStore, useUsableGameGroups } from '@/stores/gameGroups.store';
import { GroupBadge } from '@/deploy/components/ui/GroupBadge';
import { CosmeticCreate } from '@/components/config/CosmeticCreate';
import { GlyphAtlasEditor } from '@/components/config/GlyphAtlasEditor';
import {
  GradeSettingsPanel, useGradeSettingsPlace, type GradeSettingsTab,
} from '@/components/config/GradeCosmetics';
import { isProtectedGrade, loadGradesAcrossGroups, type GradesAcrossGroups } from './gradesByGameGroup';
import styles from './GradesPage.module.scss';

/**
 * Cosmétiques des grades (Configuration) : le CATALOGUE des options proposées aux grades, c'est-à-
 * dire la création des ornements (nametags), des rank tags et l'atlas de glyphes.
 *
 * Le choix d'un grade (son ornement, son rank tag, son rendu du chat, sa boutique) se fait dans
 * Supervision > Grades, onglets Cosmétiques et Boutique du détail d'un grade, sous le droit qui le
 * gardait ici (config.grades). Ce droit seul n'ouvre rien sur cette page : elle le dit et renvoie
 * vers Supervision. L'ancienne adresse `?grade=modo` mène directement au grade.
 *
 * Repli : module Grades in-game coupé pour le projet, la page Grades est fermée. Le réglage de
 * chaque grade revient alors ici (onglet Grades, sous config.grades), comme avant son déplacement :
 * personne ne le perd parce qu'un module voisin est coupé.
 */
type PageTab = 'grades' | 'creation';

export function GradeCosmeticsPage() {
  const { hasPage } = useAuthStore();
  const canCatalog = hasPage('config.cosmetics');
  // Réglage de chaque grade : en Supervision, ou ici quand Grades in-game est coupé (repli).
  const place = useGradeSettingsPlace();
  const projectName = useGameStore((s) => getGameById(s.activeGameId).name);
  const [searchParams] = useSearchParams();
  const grade = searchParams.get('grade');
  const [tab, setTab] = useState<PageTab>(
    searchParams.get('tab') === 'creation' && canCatalog ? 'creation' : 'grades',
  );

  // Ancien lien vers les réglages d'un grade : même grade, onglet Cosmétiques, dans Supervision.
  if (grade && place === 'supervision') {
    return <Navigate to={`/grades?grade=${encodeURIComponent(grade)}&tab=cosmetiques`} replace />;
  }

  const fallback = place === 'configuration';
  const toSupervision = place === 'supervision' && (
    <Link to="/grades?tab=cosmetiques" className={styles.crossLink} style={{ alignSelf: 'flex-start' }}
      title="Ornement, rank tag, chat et boutique de chaque grade">
      <Award size={14} /> Régler un grade : Supervision &gt; Grades
    </Link>
  );

  if (!canCatalog && !fallback) {
    return (
      <div className={styles.page}>
        <div className={styles.detailEmpty}>
          <Sparkles size={48} strokeWidth={1.5} />
          {place === 'supervision' ? (
            <p>
              L'ornement, le rank tag, le chat et la boutique d'un grade se règlent dans Supervision &gt;
              Grades, dans le détail du grade.
            </p>
          ) : (
            <p>Tu n'as pas accès au catalogue des cosmétiques sur ce projet.</p>
          )}
          {toSupervision}
        </div>
      </div>
    );
  }

  // Onglet affiché : relu à chaque rendu, un changement de projet peut retirer l'un des deux.
  const shown: PageTab = !fallback ? 'creation' : !canCatalog ? 'grades' : tab;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Cosmétiques des grades</h1>
        <p className={styles.subtitle}>
          {shown === 'grades'
            ? 'Ornement, rank tag, chat et boutique de chaque grade, réglés une fois pour tous les containers de configuration du projet.'
            : fallback
              ? "Créez les ornements (nametags) et rank tags proposés aux grades, et l'atlas de glyphes. Chaque grade choisit les siens dans l'onglet Grades."
              : "Créez les ornements (nametags) et rank tags proposés aux grades, et l'atlas de glyphes. Chaque grade choisit les siens dans Supervision > Grades, onglet Cosmétiques de son détail."}
        </p>
      </header>

      {fallback && canCatalog && (
        <nav className={styles.tabBar}>
          <button type="button" className={`${styles.tab} ${shown === 'grades' ? styles.tabActive : ''}`}
            onClick={() => setTab('grades')}>
            <Award size={15} /> Grades
          </button>
          <button type="button" className={`${styles.tab} ${shown === 'creation' ? styles.tabActive : ''}`}
            onClick={() => setTab('creation')}>
            <Sparkles size={15} /> Création
          </button>
        </nav>
      )}

      {shown === 'grades' ? (
        <>
          <div className={styles.protectedNotice}>
            <AlertTriangle size={16} /> Grades in-game est coupé pour {projectName} (Paramètres généraux &gt;
            Fonctionnalités) : le réglage de chaque grade se fait ici tant que le module reste coupé.
          </div>
          <GradeSettingsFallback initialGrade={grade} />
        </>
      ) : (
        <>
          {toSupervision}
          <section className={styles.detail}>
            <CosmeticCreate />
          </section>
          <section className={styles.detail}>
            <GlyphAtlasEditor page="F1" />
          </section>
        </>
      )}
    </div>
  );
}

/**
 * Repli du réglage de chaque grade : liste des grades (union des LuckPerms des groupes de
 * l'affectation du membre du staff, chaque grade avec les groupes où il existe) et, pour le grade
 * choisi, les mêmes onglets Cosmétiques et Boutique que son détail en Supervision.
 */
function GradeSettingsFallback({ initialGrade }: { initialGrade: string | null }) {
  const isAdmin = useAuthStore((s) => !!s.user?.isAdmin);
  const gameId = useGameStore((s) => s.activeGameId);
  const supportsGroups = supportsGameGroups(gameId);
  // Groupes lisibles : l'affectation du membre du staff, acceptés par la passerelle.
  const usable = useUsableGameGroups(gameId);
  const groupsPending = useGameGroupsStore((s) => supportsGroups && !s.entries[gameId]
    && (s.status === 'idle' || s.status === 'loading'));
  const [list, setList] = useState<GradesAcrossGroups | null>(null);
  const [selected, setSelected] = useState<string | null>(initialGrade ? initialGrade.toLowerCase() : null);
  // Onglet gardé d'un grade à l'autre, comme dans Supervision.
  const [tab, setTab] = useState<GradeSettingsTab>('cosmetiques');

  useEffect(() => {
    if (groupsPending) return; // les groupes arrivent : l'union se fera sur eux
    let cancelled = false;
    void loadGradesAcrossGroups(supportsGroups ? usable : []).then((res) => {
      if (!cancelled) setList(res);
    });
    return () => { cancelled = true; };
  }, [groupsPending, supportsGroups, usable]);

  if (!list) {
    return <div className={styles.loadingState}><div className={styles.spinner} /><span>Chargement...</span></div>;
  }

  const multi = list.unionOf.length > 1;
  const selectedGrade = list.grades.find((g) => g.name.toLowerCase() === selected?.toLowerCase()) ?? null;
  // Grade protégé (whitelist admin, et `admin` toujours) : lecture seule, SAUF pour un admin panel.
  const readOnly = !!selectedGrade && isProtectedGrade(selectedGrade.name, list.protectedGrades) && !isAdmin;

  return (
    <div className={styles.layout}>
      <aside className={styles.list}>
        <div className={styles.listHeader}>
          <Award size={18} strokeWidth={2} />
          <span>Grades ({list.grades.length})</span>
        </div>
        {multi && (
          <p className={styles.muted}>
            Grades lus dans le LuckPerms de {list.unionOf.map((g) => g.label).join(', ')}.
          </p>
        )}
        {list.unreadable.length > 0 && (
          <p className={styles.error}>
            Illisible : {list.unreadable.join(', ')}. Les grades qui n'existent que là n'apparaissent pas.
          </p>
        )}
        {list.error && <p className={styles.error}>Grades illisibles : {list.error}</p>}
        <div className={styles.listScroll}>
          {list.grades.length === 0 && !list.error && <p className={styles.muted}>Aucun grade in-game trouvé.</p>}
          {list.grades.map((g) => (
            <button
              key={g.name}
              className={`${styles.row} ${selectedGrade?.name === g.name ? styles.rowActive : ''}`}
              onClick={() => setSelected(g.name)}
            >
              <Award size={16} className={styles.rowIcon} />
              <span className={styles.rowName}>{g.name}</span>
              {multi && (
                <span className={styles.rowGroups}>
                  {list.unionOf.filter((u) => g.groups.includes(u.key)).map((u) => (
                    <GroupBadge key={u.key} group={u} title={`Grade présent dans le LuckPerms de ${u.label}`} />
                  ))}
                </span>
              )}
            </button>
          ))}
        </div>
      </aside>

      <section className={styles.detail}>
        {selectedGrade ? (
          <div key={selectedGrade.name} className={styles.detailInner}>
            <div className={styles.detailHeader}>
              <div className={styles.detailIdentity}>
                <Award size={22} className={styles.detailIcon} />
                <span className={styles.detailName}>{selectedGrade.name}</span>
              </div>
            </div>
            <div className={styles.tabBar} role="tablist" aria-label={`Réglages du grade ${selectedGrade.name}`}>
              <button type="button" role="tab" aria-selected={tab === 'cosmetiques'}
                className={`${styles.tab} ${tab === 'cosmetiques' ? styles.tabActive : ''}`} onClick={() => setTab('cosmetiques')}>
                <Sparkles size={14} /> Cosmétiques
              </button>
              <button type="button" role="tab" aria-selected={tab === 'boutique'}
                className={`${styles.tab} ${tab === 'boutique' ? styles.tabActive : ''}`} onClick={() => setTab('boutique')}>
                <Store size={14} /> Boutique
              </button>
            </div>
            {readOnly && (
              <div className={styles.protectedNotice}>
                <ShieldCheck size={16} /> Le grade <strong>{selectedGrade.name}</strong> est protégé : édition réservée aux
                admins (lecture seule).
              </div>
            )}
            {/* Le catalogue est l'onglet Création de cette page : pas de lien vers elle-même. */}
            <GradeSettingsPanel grade={selectedGrade.name} tab={tab} readOnly={readOnly} catalogLink={false} />
          </div>
        ) : (
          <div className={styles.detailEmpty}>
            <Sparkles size={48} strokeWidth={1.5} />
            <p>Sélectionne un grade pour régler son ornement, son rank tag, son chat et sa boutique.</p>
          </div>
        )}
      </section>
    </div>
  );
}
